import styled from 'styled-components';

import { bidiText } from 'components/bidi';

/**
 * The table from the design system.
 *
 *   thead  uppercase --fs-th on --surface-alt, one hairline underneath
 *   tbody  --h-row rows, hairline separators, --surface-alt on hover
 *
 * Alignment and cell typography come from the column's `type` (and, for the
 * call sites that predate it, from `dir`); AppTable turns both into the
 * classes below. Nothing here sorts — no endpoint accepts an `orderBy`.
 */

export const TableCon = styled.div`
  overflow-x: auto;
  width: 100%;
  position: relative;
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  /*
   * Fixed layout, so the widths declared on the headers are the widths the
   * columns get. Under the browser's default auto layout every declared
   * width is only a suggestion the content can overrule, which is why the
   * old percentages did nothing: a long value simply pushed its neighbours
   * out of shape. Columns that declare no width share whatever is left —
   * that is the payload column, and it is meant to absorb the slack.
   */
  table-layout: fixed;
`;

export const Thead = styled.thead`
  background: var(--surface-alt);
`;

export const Th = styled.th`
  padding: var(--s2) var(--s4);
  border-bottom: 1px solid var(--line);
  font-family: var(--font-ui);
  font-size: var(--fs-th);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-body);
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text-faint);
  white-space: nowrap;
  overflow: hidden;
  vertical-align: middle;

  &.align-left {
    text-align: left;
  }

  &.align-right {
    text-align: right;
  }

  &.align-center {
    text-align: center;
  }
`;

export const HeaderTh = styled.div`
  display: flex;
  align-items: center;
  gap: var(--s1);
  line-height: inherit;
  color: inherit;

  svg {
    width: 15px;
    height: 15px;
    stroke-width: 1.8;
  }
`;

export const Tr = styled.tr`
  position: relative;

  &.active td {
    color: var(--primary);
    font-weight: var(--fw-semibold);
  }
`;

export const Td = styled.td`
  height: var(--h-row);
  padding: 0 var(--s4);
  border-bottom: 1px solid var(--line);
  font-family: var(--font-ui);
  font-size: var(--fs-body);
  font-weight: var(--fw-regular);
  line-height: var(--lh-body);
  color: var(--text);
  vertical-align: middle;

  /*
   * One line per cell. Every row on a table is --h-row tall, so a value that
   * does not fit is clipped with an ellipsis rather than allowed to wrap and
   * push the row out of line. AppTable puts the full value on the cell's
   * title attribute whenever it is actually clipped, so nothing becomes
   *
   * unreadable. The rule applies to the cell and to its immediate child,
   * because the call sites render either a bare value or one wrapping div.
   */
  &:not(.actions),
  &:not(.actions) > * {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /*
   * Cells hold text a person typed, in a language the payload does not
   * declare. The cell and its immediate child both take it, because a call
   * site renders either a bare value or one wrapping div — the same two
   * shapes the ellipsis rule above covers. Alignment is deliberately left to
   * the column: see the note in bidi.ts.
   */
  &:not(.actions),
  &:not(.actions) > * {
    ${bidiText};
  }

  &.align-left {
    text-align: left;
  }

  &.align-right {
    text-align: right;
  }

  &.align-center {
    text-align: center;
  }

  /*
   * Machine-generated values — ids, counts, timestamps — line up by digit,
   * and opt back out of the content treatment above: a number is not text in
   * an unknown language, and letting one auto-detect its own direction would
   * only find ways to go wrong.
   */
  &.num,
  &.num > * {
    font-family: var(--font-mono);
    font-size: var(--fs-mono);
    font-variant-numeric: tabular-nums;
    unicode-bidi: normal;
  }

  &.num {
    color: var(--text-muted);
  }

  &.actions {
    white-space: nowrap;
  }
`;

/*
 * Row actions.
 *
 * They are permanently visible. This is an internal tool whose users run the
 * same three actions dozens of times a day and are not exploring the screen —
 * an affordance that only appears once the pointer lands on a row reads as no
 * affordance at all. They stay quiet instead: faint icons that take a ground
 * only under the pointer, so twenty rows of them do not shout.
 *
 * AppTable wraps every `type: 'actions'` cell in this. What the cell holds is
 * the call site's business — some are bare icons, some are real buttons — so
 * only the icon affordances are boxed here. A ReactSVG always lands as
 * `svg.injected-svg`, which makes its immediate wrapper the box to size;
 * icons that already sit inside a button bring their own geometry and are
 * left alone.
 */
export const RowActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--s1);

  *:has(> svg.injected-svg):not(button *) {
    display: inline-grid;
    place-items: center;
    width: var(--h-btn-sm);
    height: var(--h-btn-sm);
    border-radius: var(--r-sm);
    color: var(--text-faint);
    cursor: pointer;
    transition:
      background-color 0.12s,
      color 0.12s;

    &:hover {
      background: var(--surface-alt);
      color: var(--text);
    }
  }

  svg.injected-svg {
    width: 15px;
    height: 15px;
  }

  /*
   * The call sites paint these icons with a presentation attribute
   * (stroke="#8A93AC", fill="#8A93AC"). CSS outranks a presentation
   * attribute, so the icon follows the button's colour instead of a
   * hardcoded grey — without touching a single call site.
   */
  svg.injected-svg[stroke]:not(button *) {
    stroke: currentColor;
  }

  svg.injected-svg:not(button *) [fill]:not([fill='none']) {
    fill: currentColor;
  }
`;

/* Declared after Td so the row rules can reference it. */
export const Tbody = styled.tbody`
  ${Tr}:hover {
    background: var(--surface-alt);
  }

  ${Tr}:last-child ${Td} {
    border-bottom: none;
  }
`;

export const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: var(--s5) 0;
`;

export const Status = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--s2);
  padding: var(--s1) var(--s3);
  border-radius: var(--r-pill);
  font-size: var(--fs-micro);
  font-weight: var(--fw-medium);
  line-height: var(--lh-body);

  &.Success {
    background: var(--ok-soft);
    color: var(--ok);
  }

  &.Rejected {
    background: var(--danger-soft);
    color: var(--danger);
  }
`;

export const Div = styled.div``;
